export const ANTHROPIC_BETA_HEADER = 'prompt-caching-2024-07-31,extended-cache-ttl-2025-04-11'

export function isClaudeModel(model: string): boolean {
  return model.toLowerCase().includes('claude')
}

/**
 * 判断 baseURL 是否为 Anthropic 官方端点。
 * 仅官方端点允许开启 1h extended cache TTL，且永远不会被标记为「不兼容原生格式」。
 */
const ANTHROPIC_OFFICIAL_HOSTS = new Set(['api.anthropic.com'])
export function isAnthropicOfficialHost(baseUrl: string): boolean {
  try {
    return ANTHROPIC_OFFICIAL_HOSTS.has(new URL(baseUrl).hostname.toLowerCase())
  }
  catch {
    return false
  }
}

const INCOMPATIBLE_CACHE_KEY = 'claude-caching/incompatible-base-urls'
// 1 小时：足够避免短时间内反复探测打挂代理，又不会把一次偶发 404 放大为整日降级
const INCOMPATIBLE_TTL_MS = 60 * 60 * 1000

interface IncompatibleEntry {
  ts: number
}

function isValidEntry(v: unknown): v is IncompatibleEntry {
  if (typeof v !== 'object' || v === null)
    return false
  const ts = (v as { ts?: unknown }).ts
  return typeof ts === 'number' && Number.isFinite(ts)
}

// 进程内缓存：避免每次 streamFrom 都重新 JSON.parse localStorage
let memoryMap: Record<string, IncompatibleEntry> | null = null

function loadIncompatibleMap(): Record<string, IncompatibleEntry> {
  if (memoryMap)
    return memoryMap
  try {
    const raw = localStorage.getItem(INCOMPATIBLE_CACHE_KEY)
    if (!raw) {
      memoryMap = {}
      return memoryMap
    }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      memoryMap = {}
      return memoryMap
    }
    const validated: Record<string, IncompatibleEntry> = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof k === 'string' && isValidEntry(v))
        validated[k] = { ts: v.ts }
    }
    memoryMap = validated
    return memoryMap
  }
  catch {
    memoryMap = {}
    return memoryMap
  }
}

function saveIncompatibleMap(map: Record<string, IncompatibleEntry>) {
  memoryMap = map
  try {
    localStorage.setItem(INCOMPATIBLE_CACHE_KEY, JSON.stringify(map))
  }
  catch {
    // localStorage 不可写（隐私模式 / 配额耗尽）时静默
  }
}

/** 标记某个 baseURL 不支持 Anthropic 原生格式，持久化 1 小时；官方端点永不标记。 */
export function markBaseUrlAsIncompatible(baseUrl: string): void {
  if (isAnthropicOfficialHost(baseUrl))
    return
  const map = loadIncompatibleMap()
  map[baseUrl] = { ts: Date.now() }
  saveIncompatibleMap(map)
}

/** 检查某个 baseURL 是否已被标记为不兼容（且未过期） */
export function isBaseUrlIncompatible(baseUrl: string): boolean {
  if (isAnthropicOfficialHost(baseUrl))
    return false
  const map = loadIncompatibleMap()
  const entry = map[baseUrl]
  if (!entry)
    return false
  if (Date.now() - entry.ts > INCOMPATIBLE_TTL_MS) {
    delete map[baseUrl]
    saveIncompatibleMap(map)
    return false
  }
  return true
}

/** 对消息列表原地注入 cache_control（只给第一条 system message 打缓存点）。调用前请先复制消息数组。 */
export function injectCacheControl(messages: Array<{ role: string, content: unknown }>): void {
  const systemIndices = messages
    .map((m, i) => m.role === 'system' ? i : -1)
    .filter(i => i !== -1)
  const firstSystemIndex = systemIndices[0]
  if (firstSystemIndex === undefined)
    return
  const msg = messages[firstSystemIndex]
  if (typeof msg.content === 'string' && msg.content.trim()) {
    msg.content = [{ type: 'text' as const, text: msg.content, cache_control: { type: 'ephemeral' } }] as any
  }
}

export function getClaudeHeaders(extraHeaders?: Record<string, string>): Record<string, string> {
  return { ...(extraHeaders ?? {}), 'anthropic-beta': ANTHROPIC_BETA_HEADER }
}
